
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Clock, MapPin, Phone, Star, ExternalLink, DollarSign } from "lucide-react";
import { ReviewList } from "@/components/reviews/ReviewList";
import { ReviewForm } from "@/components/reviews/ReviewForm";
import { ReviewItemProps } from "@/components/reviews/ReviewItem";

const RestaurantDetail = () => {
  const { id } = useParams();
  const [restaurant, setRestaurant] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reviews, setReviews] = useState<ReviewItemProps[]>([]);

  useEffect(() => {
    // Simulate fetching restaurant data
    const fetchRestaurant = () => {
      setLoading(true);
      
      // Mock restaurant data
      const restaurants = [
        {
          id: "1",
          name: "라스베가스 한식당",
          image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&h=500&fit=crop",
          gallery: [
            "https://images.unsplash.com/photo-1498654896293-37aacf113fd9?w=800&h=500&fit=crop",
            "https://images.unsplash.com/photo-1559847844-5315695dadae?w=800&h=500&fit=crop",
            "https://images.unsplash.com/photo-1533640924469-304ea0332bd9?w=800&h=500&fit=crop",
          ],
          category: "한식",
          rating: 4.8,
          reviews: 128,
          priceLevel: "$$",
          location: "스트립 중심부, 라스베가스",
          address: "123 Las Vegas Blvd, Las Vegas, NV 89109",
          hours: "매일 11:00 AM - 9:30 PM",
          phone: "(702) 123-4567",
          website: "https://example.com/lasvegas-koreancuisine",
          description: "정통 한국 음식을 맛볼 수 있는 라스베가스 최고의 한식당입니다. 신선한 재료로 만든 다양한 한식 메뉴가 준비되어 있으며, 특히 불고기와 김치찌개로 유명합니다. 가족 경영으로 한국인 쉐프가 직접 요리하며, 현지인과 관광객 모두에게 사랑받는 식당입니다.",
          badges: ["인기", "가족 친화적", "예약 필수"],
          popularDishes: [
            "불고기",
            "김치찌개",
            "비빔밥",
            "된장찌개",
            "갈비"
          ],
          customerReviews: [
            {
              author: "김지영",
              avatar: "",
              rating: 5,
              date: new Date("2023-10-15"),
              content: "라스베가스에서 진짜 한국 맛을 느낄 수 있는 곳입니다! 김치찌개가 정말 한국에서 먹는 맛 그대로였어요. 직원분들도 친절하고 서비스도 좋았습니다. 라스베가스 방문 시 꼭 들러보세요."
            },
            {
              author: "이민수",
              avatar: "",
              rating: 4,
              date: new Date("2023-09-28"),
              content: "불고기와 갈비가 정말 맛있었습니다. 양도 생각보다 많아서 좋았어요. 다만 주말에는 사람이 많아 예약이 필수입니다. 가족들과 방문하기 좋은 곳입니다."
            },
            {
              author: "최서연",
              avatar: "",
              rating: 5,
              date: new Date("2023-08-05"),
              content: "여행 중에 한국 음식이 그리워서 방문했는데, 정말 기대 이상이었습니다. 비빔밥의 고추장 맛이 정말 본토 그대로였어요. 반찬도 푸짐하게 나와서 좋았습니다."
            }
          ]
        }
      ];
      
      const found = restaurants.find(r => r.id === id);
      
      if (found) {
        setRestaurant(found);
      }
      
      setLoading(false);
    };
    
    fetchRestaurant();
  }, [id]);

  const handleReviewSubmit = (rating: number, content: string) => {
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      const newReview: ReviewItemProps = {
        author: "사용자", // In a real app, this would be the logged-in user
        avatar: "",
        rating,
        date: new Date(),
        content
      };
      
      setReviews(prev => [newReview, ...prev]);
      setIsSubmitting(false);
    }, 1000);
  };

  if (loading) {
    return (
      <Layout>
        <div className="container py-8">
          <div className="flex justify-center items-center h-96">
            <div className="animate-pulse text-primary">로딩 중...</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!restaurant) {
    return (
      <Layout>
        <div className="container py-8">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold mb-4">식당을 찾을 수 없습니다</h2>
            <Button asChild>
              <Link to="/travel">맛집/여행지 목록으로 돌아가기</Link>
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  // Combine mock reviews with user-added reviews
  const allReviews = [...(restaurant.customerReviews || []), ...reviews];

  return (
    <Layout>
      <div className="container py-8">
        <Button variant="ghost" asChild className="mb-4">
          <Link to="/travel" className="flex items-center gap-1 text-muted-foreground hover:text-primary">
            <ArrowLeft className="h-4 w-4" />
            <span>맛집/여행지 목록으로 돌아가기</span>
          </Link>
        </Button>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          <div className="md:col-span-2">
            <h1 className="text-3xl font-bold mb-2">{restaurant.name}</h1>
            <div className="flex flex-wrap items-center gap-2 text-muted-foreground mb-4">
              <Badge className="bg-primary text-white">{restaurant.category}</Badge>
              <div className="flex items-center gap-1 text-amber-500">
                <Star className="h-4 w-4 fill-current" />
                <span className="font-medium">{restaurant.rating}</span>
                <span className="text-muted-foreground text-sm">({restaurant.reviews} 리뷰)</span>
              </div>
              <div className="flex items-center gap-1">
                <DollarSign className="h-4 w-4" />
                <span>{restaurant.priceLevel}</span>
              </div>
            </div>

            <div 
              className="w-full h-80 rounded-lg mb-6 bg-cover bg-center"
              style={{ backgroundImage: `url(${restaurant.image})` }}
            />

            <div className="grid grid-cols-3 gap-4 mb-6">
              {restaurant.gallery?.map((img: string, index: number) => (
                <img 
                  key={index} 
                  src={img} 
                  alt={`${restaurant.name} ${index+1}`} 
                  className="w-full h-32 object-cover rounded-lg"
                />
              ))}
            </div>

            <div className="space-y-6 mb-8">
              <div>
                <h2 className="text-xl font-semibold mb-3">소개</h2>
                <p className="text-muted-foreground">{restaurant.description}</p>
              </div>

              <div>
                <h2 className="text-xl font-semibold mb-3">인기 메뉴</h2>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {restaurant.popularDishes?.map((dish: string, index: number) => (
                    <li key={index} className="flex items-center gap-2">
                      <span className="text-primary">•</span>
                      <span>{dish}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-wrap gap-2">
                {restaurant.badges?.map((badge: string, index: number) => (
                  <Badge key={index} variant="outline">
                    {badge}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <ReviewList reviews={allReviews} title="방문자 후기" />
              
              <div className="mt-6">
                <ReviewForm onSubmit={handleReviewSubmit} isSubmitting={isSubmitting} />
              </div>
            </div>
          </div>

          <div>
            <Card className="sticky top-6">
              <CardContent className="p-5">
                <h3 className="font-medium text-lg mb-4">정보</h3>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground shrink-0" />
                    <div>
                      <p className="font-medium">주소</p>
                      <p className="text-muted-foreground">{restaurant.address}</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Clock className="h-5 w-5 text-muted-foreground shrink-0" />
                    <div>
                      <p className="font-medium">영업시간</p>
                      <p className="text-muted-foreground">{restaurant.hours}</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Phone className="h-5 w-5 text-muted-foreground shrink-0" />
                    <div>
                      <p className="font-medium">전화</p>
                      <p className="text-muted-foreground">{restaurant.phone}</p>
                    </div>
                  </div>
                  {restaurant.website && (
                    <div className="flex gap-3">
                      <ExternalLink className="h-5 w-5 text-muted-foreground shrink-0" />
                      <div>
                        <p className="font-medium">웹사이트</p>
                        <a 
                          href={restaurant.website} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-primary hover:underline"
                        >
                          {restaurant.website.replace(/^https?:\/\//, '')}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-6 space-y-3">
                  <Button className="w-full">예약하기</Button>
                  <Button variant="outline" className="w-full">길찾기</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RestaurantDetail;
